import { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const SUGGESTIONS = ["What fees do you charge?", "Where is my last withdrawal?", "How does auto-buy work?"];

async function streamChat(message, onDelta) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/chat`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message }),
  });
  if (!res.ok) throw new Error("The assistant is unavailable right now");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const ev = JSON.parse(d);
      if (ev.error) throw new Error(ev.error);
      onDelta(ev.delta);
    }
  }
}

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.role}-${i}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "rounded-br-sm bg-[#F7931A] text-[#08090E]" : "rounded-bl-sm border border-[#212638] bg-[#0F111A] text-[#F8FAFC]"}`}>
      {m.content || <Loader2 size={14} className="animate-spin text-[#F7931A]" />}
    </div>
  </div>
);

export const SupportChat = () => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => { api.get("/support/history").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const append = (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; });
    try {
      await streamChat(message, append);
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => (m[m.length - 1].content ? m : m.slice(0, -1)));
    }
    setBusy(false);
  };

  const reset = () => api.delete("/support/history").then(() => setMsgs([])).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="flex h-[26rem] flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto pr-1" data-testid="support-chat-messages">
        {msgs.length === 0 && (
          <div className="space-y-2 pt-2">
            <p className="text-sm text-[#94A3B8]">Hi! I can answer questions about buying, selling, fees, withdrawals and your account.</p>
            {SUGGESTIONS.map((s, i) => (
              <button key={s} onClick={() => send(s)} data-testid={`support-suggestion-${i}`}
                className="block w-full rounded-xl border border-[#212638] px-3 py-2 text-left text-xs text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">{s}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        <div ref={end} />
      </div>
      <div className="mt-3 flex gap-2">
        {msgs.length > 0 && (
          <button onClick={reset} title="New chat" data-testid="support-new-chat-button" className="rounded-xl border border-[#212638] px-3 text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white"><RotateCcw size={14} /></button>
        )}
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask a question..."
          maxLength={2000} data-testid="support-chat-input"
          className="flex-1 rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#F7931A]" />
        <button onClick={() => send()} disabled={busy || !text.trim()} data-testid="support-chat-send-button"
          className="rounded-xl bg-[#F7931A] px-3.5 text-[#08090E] transition-colors hover:bg-amber-500 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
};
